import type { ReactNode } from 'react';

import { STEPS, type StepId } from './workflowShared';

interface WorkflowShellProps {
  currentStep: StepId;
  stepperLabel: string;
  stepLabels?: Partial<Record<StepId, string>>;
  headerActions?: ReactNode;
  overlay?: ReactNode;
  summary?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}

interface WorkflowHeaderProps {
  currentStep: StepId;
  stepperLabel: string;
  stepLabels?: Partial<Record<StepId, string>>;
  actions?: ReactNode;
}

function WorkflowHeader({
  currentStep,
  stepperLabel,
  stepLabels = {},
  actions
}: WorkflowHeaderProps) {
  const currentStepIndex = STEPS.findIndex((step) => step.id === currentStep);

  return (
    <header className="top-bar">
      <a className="logo" href="/">
        <img className="logo-mark" src="/logoinvert.png" alt="" aria-hidden="true" />
        <span className="logo-text">Havu</span>
      </a>
      <div className="top-actions">
        <nav className="stepper" aria-label={stepperLabel}>
          {STEPS.map((step, index) => {
            const isActive = step.id === currentStep;
            const isComplete = currentStepIndex > index;

            return (
              <div className={`step-item ${isActive ? 'active' : ''} ${isComplete ? 'complete' : ''}`} key={step.id}>
                <span className="step-index">{index + 1}</span>
                <span className="step-label">{stepLabels[step.id] ?? step.label}</span>
              </div>
            );
          })}
        </nav>
        {actions}
      </div>
    </header>
  );
}

function WorkflowShell({
  currentStep,
  stepperLabel,
  stepLabels,
  headerActions,
  overlay,
  summary,
  footer,
  children
}: WorkflowShellProps) {
  return (
    <div className="app-shell">
      {overlay}

      <WorkflowHeader
        currentStep={currentStep}
        stepperLabel={stepperLabel}
        stepLabels={stepLabels}
        actions={headerActions}
      />

      <main className={`workspace ${currentStep === 'pricing' ? 'full-width' : ''}`}>
        <section className="workflow" aria-live="polite">
          {children}
        </section>

        {currentStep !== 'pricing' && summary}
      </main>

      {footer}
    </div>
  );
}

export default WorkflowShell;
